import styled from 'styled-components';
import { color } from 'components/base/color';

interface TextProps {
  bold?: boolean;
}

interface HorizontalLineProps {
  width?: string;
  size: number;
  border?: string;
}

const BillWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const MerchantRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
`;

const MerchantText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const MerchantBrand = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroAmountSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 14px;
  padding-bottom: 14px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 2px;
    border-radius: 1px;
    background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  }
`;

const HeroAmountLabel = styled.p`
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 500;
  margin: 0;
`;

const BillContent = styled.div`
  display: flex;
  width: 100%;
  margin-top: 8px;
  padding: 10px 0 0;
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border-radius: 0;
  border: 0;
  box-shadow: none;

  @media (min-width: 768px) {
    margin-top: 10px;
    padding: 10px 0 0;
  }
`;

const MerchantName = styled.p`
  ${({ theme: { typography } }) => typography.field()};
  color: #0f172a;
  text-align: center;
  font-weight: 700;
  font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  margin-top: 0;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MerchantSubline = styled.p`
  margin: 2px 0 0;
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  text-align: center;
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  line-height: 0;
`;

const Invoice = styled(Item)`
  color: ${({ theme: { color } }) => color.gray_400};
  font-weight: bold;
  font-size: 12px;
  line-height: 16px;
`;

const Name = styled.p<TextProps>`
  font-size: 16px;
  color: ${color.gray_700};
  font-weight: ${({ bold }) => (bold ? 'bold' : 'normal')};
`;

const Price = styled.p<TextProps>`
  text-align: right;
  font-size: 16px;
  color: ${color.gray_700};
  font-weight: ${({ bold }) => (bold ? 'bold' : 'normal')};
`;

const LargePrice = styled(Price)`
  font-size: 54px;
  font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  margin: 2px 0 0;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #0f172a;
  line-height: 1;

  @media (min-width: 768px) {
    font-size: 64px;
  }
`;

const Description = styled(Name)`
  font-size: 14px;
  color: ${({ theme: { color } }) => color.gray_400};
`;

const Field = styled(Description)`
  color: ${({ theme: { color } }) => color.black};
  font-weight: 700;
`;

const Box = styled.div`
  width: 0;
`;

const HorizontalLine = styled.hr<HorizontalLineProps>`
  margin-top: 10px;
  margin-bottom: 14px;
  width: 100%;
  border: 0;
  border-bottom: ${({ border, size }) =>
    `${size}px ${border ? border : 'solid'} ${color.white_700}`};
`;

const DetailEmptyMenuItem = styled.div`
  display: 'flex';
  flex-direction: 'center';
`;

const SubTotalContainer = styled.div`
  margin-bottom: 5px;
`;

const PaymentMeta = styled.div`
  width: 100%;
  margin-top: 6px;
`;

const PaymentMetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
`;

const PaymentMetaLabel = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const PaymentMetaValue = styled.span`
  font-size: 12px;
  color: #0f172a;
  font-weight: 600;
  max-width: 65%;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PreAuthNotice = styled.div`
  width: 100%;
  margin-top: 10px;
  padding: 10px 14px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.5;
  color: #0c4a6e;

  strong {
    font-weight: 700;
    color: #075985;
  }
`;

export {
  BillWrapper,
  MerchantRow,
  MerchantText,
  MerchantBrand,
  HeroAmountSection,
  HeroAmountLabel,
  BillContent,
  MerchantName,
  MerchantSubline,
  Item,
  Invoice,
  Name,
  Price,
  Description,
  Field,
  PreAuthNotice,
  Box,
  HorizontalLine,
  DetailEmptyMenuItem,
  LargePrice,
  SubTotalContainer,
  PaymentMeta,
  PaymentMetaRow,
  PaymentMetaLabel,
  PaymentMetaValue,
};
